/**
 * SSE integration for chat real-time updates (issue #351).
 *
 * Reuses the existing ``/api/events`` EventSource (no second connection)
 * — listens for ``chat:bootstrap``, ``chat:message.new``,
 * ``chat:message.read``, ``chat:message.deleted`` and invalidates the
 * matching React Query keys.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { chatKeys } from './useChatQueries';

type ChatSSEEvent =
  | 'chat:bootstrap'
  | 'chat:message.new'
  | 'chat:message.read'
  | 'chat:message.deleted'
  | 'chat:unread.count';

interface UseChatSSEOptions {
  enabled?: boolean;
}

export function useChatSSE(options: UseChatSSEOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!enabled) return;

    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const eventSource = new EventSource(`${API_URL}/events`, { withCredentials: true });

    const invalidateForEvent = (type: ChatSSEEvent, payload: unknown) => {
      const qc = queryClientRef.current;
      const data = (payload || {}) as { thread_id?: string };

      switch (type) {
        case 'chat:bootstrap':
          // Initial snapshot — invalidate everything so the widget
          // picks up the per-tab unread badges + the techsupport
          // thread without a manual refetch.
          qc.invalidateQueries({ queryKey: chatKeys.all });
          break;
        case 'chat:message.new':
        case 'chat:message.deleted':
          qc.invalidateQueries({ queryKey: chatKeys.threads() });
          qc.invalidateQueries({ queryKey: chatKeys.adminThreads() });
          qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
          qc.invalidateQueries({ queryKey: chatKeys.unread() });
          if (data.thread_id) {
            qc.invalidateQueries({ queryKey: chatKeys.threadMessages(data.thread_id) });
          }
          break;
        case 'chat:message.read':
        case 'chat:unread.count':
          qc.invalidateQueries({ queryKey: chatKeys.unread() });
          // The launcher badge is backed by ``useChatThreads()`` /
          // ``chatKeys.threads()``, so a read receipt arriving from
          // another tab must invalidate that query too — otherwise
          // ``thread.unread_count`` stays at its pre-read value until
          // the 60s staleTime expires (or the window refocuses), and
          // the FAB keeps pulsing with a stale red badge after the
          // server has already marked the messages read.
          qc.invalidateQueries({ queryKey: chatKeys.threads() });
          qc.invalidateQueries({ queryKey: chatKeys.adminThreads() });
          qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
          if (data.thread_id) {
            qc.invalidateQueries({ queryKey: chatKeys.threadMessages(data.thread_id) });
          }
          break;
      }
    };

    const chatEventTypes: ChatSSEEvent[] = [
      'chat:bootstrap',
      'chat:message.new',
      'chat:message.read',
      'chat:message.deleted',
      'chat:unread.count',
    ];

    const listeners: Array<[string, (e: MessageEvent) => void]> = [];
    for (const eventType of chatEventTypes) {
      const handler = (e: MessageEvent) => {
        let payload: unknown = null;
        try {
          payload = e.data ? JSON.parse(e.data as string) : null;
        } catch {
          payload = null;
        }
        invalidateForEvent(eventType, payload);
      };
      eventSource.addEventListener(eventType, handler);
      listeners.push([eventType, handler]);
    }

    return () => {
      for (const [type, handler] of listeners) {
        eventSource.removeEventListener(type, handler);
      }
      eventSource.close();
    };
  }, [enabled]);
}
